import { createTheme, localStorageColorSchemeManager, MantineTheme } from '@mantine/core';

/**
 * Application theme configuration for Mantine with encapsulated theme options (registry).
 *
 * Quick start:
 * - Choose a theme by setting Vite env var VITE_APP_THEME to one of the keys in `appThemes` (e.g., 'default').
 * - Or change `APP_THEME_NAME` below.
 * - To customize brand/status/surfaces globally, edit tokens here in one place.
 */

// Shared brand palettes used by themes. Keep this list small and semantic.
const brandPalettes = {
  terrorist: [
    '#F8F6F3',
    '#E9E0D5',
    '#DFCCB7',
    '#DABB97',
    '#DCAC73',
    '#F7931E',
    '#DA8420',
    '#956935',
    '#7E5E38',
    '#5C4B38',
  ],
  counterterrorist: [
    '#58738E',
    '#4A6887',
    '#3C5F82',
    '#2F567E',
    '#224E7C',
    '#14477B',
    '#07407B',
    '#113A64',
    '#1A2F45',
    '#1B2A3A',
  ],
} as const;

// Minimal, semantic tokens: surfaces + status + franchiseColor.
// Change these to adjust backgrounds and status colors app-wide.
const otherTokens = {
  surfaces: {
    // Tracks Mantine body background variable to avoid mismatches in dark/light.
    body: 'var(--mantine-color-body)',
    card: 'var(--mantine-color-body)',
    subtle: 'var(--mantine-color-body)'
  },
  status: {
    win: 'green',
    lose: 'red',
    draw: 'gray'
  },
  // Franchise badge color; change to rebrand franchise chips globally.
  franchiseColor: 'terrorist'
} as const;

// Common component defaults for all themes (kept small, scheme-aware where possible).
const commonComponents = {
  Card: {
    defaultProps: {
      withBorder: true,
      radius: 'md',
      p: 'md',
    },
    styles: (theme: MantineTheme) => ({
      root: {
        // Use themed surfaces to maintain contrast in both schemes.
        // To customize card background, edit other.surfaces.card in this file.
        backgroundColor: (theme.other as any)?.surfaces?.card ?? 'var(--mantine-color-body)'
      },
    }),
  },
  Badge: {
    defaultProps: {
      // Filled provides better legibility on dark surfaces; components may override per-instance.
      variant: 'filled',
    },
  },
  Button: {
    defaultProps: {
      radius: 'md',
    },
  },
  Title: {
    defaultProps: {
      order: 4,
    },
    styles: () => ({
      root: {
        // Ensure proper text color in both light and dark modes
        color: 'var(--mantine-color-text)',
      },
    }),
  },
} as const;

// Theme registry: add new themes here.
export const appThemes = {
  /** Default brand theme (blue primary) */
  default: createTheme({
    primaryColor: 'counterterrorist',
    primaryShade: { light: 6, dark: 4 },
    fontFamily: 'Roboto, sans-serif',
    fontFamilyMonospace: 'Monaco, Courier, monospace',
    headings: { fontFamily: 'Exo, sans-serif' },
    colors: brandPalettes,
    other: otherTokens,
    components: commonComponents as any,
  }),

  /** Citrus theme (orange primary using `terrorist` palette) */
  citrus: createTheme({
    primaryColor: 'terrorist',
    primaryShade: { light: 6, dark: 5 },
    fontFamily: 'Roboto, sans-serif',
    fontFamilyMonospace: 'Monaco, Courier, monospace',
    headings: { fontFamily: 'Exo, sans-serif' },
    colors: brandPalettes,
    other: { ...otherTokens, franchiseColor: 'counterterrorist' },
    components: commonComponents as any,
  }),

  /** Midnight theme (darker blue emphasis) */
  midnight: createTheme({
    primaryColor: 'counterterrorist',
    primaryShade: { light: 7, dark: 3 },
    fontFamily: 'Roboto, sans-serif',
    fontFamilyMonospace: 'Monaco, Courier, monospace',
    headings: { fontFamily: 'Exo, sans-serif' },
    colors: brandPalettes,
    other: otherTokens,
    components: commonComponents as any,
  }),
} as const;

export type AppThemeName = keyof typeof appThemes;

// Resolve a theme key from Vite env if provided; fallback to 'default'.
const envKey = (import.meta as any)?.env?.VITE_APP_THEME as AppThemeName | undefined;
export const APP_THEME_NAME: AppThemeName = envKey && envKey in appThemes ? envKey : 'default';

// Export the active theme used by MantineProvider.
export const appTheme = appThemes[APP_THEME_NAME];

/**
 * Color scheme persistence manager.
 *
 * How to modify:
 * - Switch to `cookieStorageColorSchemeManager()` to persist in cookies if needed.
 * - Set `getInitialValueInEffect` to true to avoid hydration mismatches in SSR.
 */
export const colorSchemeManager = localStorageColorSchemeManager({ key: 'csc-color-scheme' });

export type AppTheme = typeof appTheme;
